<template>
  <div class="parking-space-apply-manage">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('parkingSpaceApplyManage.page.title')"
      :subtitle="$t('parkingSpaceApplyManage.page.subtitle')">
      <template #extra>
        <el-button type="primary" icon="el-icon-plus" @click="_openAddParkingSpaceApplyModal">
          {{ $t('parkingSpaceApplyManage.applyForSpace') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="conditions.carNum" clearable class="w-full"
            :placeholder="$t('parkingSpaceApplyManage.licensePlate')" @keyup.enter.native="_queryParkingSpaceApplyMethod" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="conditions.carBrand" clearable class="w-full"
            :placeholder="$t('parkingSpaceApplyManage.carBrand')" @keyup.enter.native="_queryParkingSpaceApplyMethod" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="conditions.applyPersonName" clearable class="w-full"
            :placeholder="$t('parkingSpaceApplyManage.applicantName')"
            @keyup.enter.native="_queryParkingSpaceApplyMethod" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="conditions.applyPersonLink" clearable class="w-full"
            :placeholder="$t('parkingSpaceApplyManage.applicantPhone')"
            @keyup.enter.native="_queryParkingSpaceApplyMethod" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-select v-model="conditions.state" clearable class="w-full"
            :placeholder="$t('parkingSpaceApplyManage.selectStatus')">
            <el-option :label="$t('parkingSpaceApplyManage.selectStatus')" value="" />
            <el-option :label="$t('parkingSpaceApplyManage.pendingReview')" value="1001" />
            <el-option :label="$t('parkingSpaceApplyManage.pendingPayment')" value="2002" />
            <el-option :label="$t('parkingSpaceApplyManage.completed')" value="3003" />
            <el-option :label="$t('parkingSpaceApplyManage.applicationFailed')" value="4004" />
          </el-select>
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="_queryParkingSpaceApplyMethod">
          {{ $t('parkingSpaceApplyManage.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="_resetParkingSpaceApplyMethod">
          {{ $t('parkingSpaceApplyManage.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="page.total" :density="density" :show-column-setting="false"
        :show-refresh="true" @update:density="density = $event" @refresh="handleSuccess">
        <template #left>
          <span class="ds-panel__title">{{ $t('parkingSpaceApplyManage.parkingSpaceApplication') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="parkingSpaceApplys" :loading="loading" :error="hasError" :error-message="errorMessage"
        :density="density" border :empty-description="$t('parkingSpaceApplyManage.page.empty')" @retry="handleSuccess">
        <el-table-column prop="applyId" :label="$t('parkingSpaceApplyManage.applicationId')" align="center" width="150">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.applyId || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="carNum" :label="$t('parkingSpaceApplyManage.licensePlate')" align="center" width="120">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.carNum || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('parkingSpaceApplyManage.parkingSpace')" align="center" width="140">
          <template slot-scope="scope">
            <span v-if="scope.row.psId" class="ds-num">{{ scope.row.areaNum }} - {{ scope.row.num }}</span>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="carBrand" :label="$t('parkingSpaceApplyManage.carBrand')" align="center" width="110" />
        <el-table-column :label="$t('parkingSpaceApplyManage.vehicleType')" align="center" width="120">
          <template slot-scope="scope">
            <ds-status-tag size="sm" type="muted" :label="_getCatType(scope.row.carType)" />
          </template>
        </el-table-column>
        <el-table-column prop="carColor" :label="$t('parkingSpaceApplyManage.color')" align="center" width="90" />
        <el-table-column prop="startTime" :label="$t('parkingSpaceApplyManage.startTime')" align="center" width="170">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.startTime || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="endTime" :label="$t('parkingSpaceApplyManage.endTime')" align="center" width="170">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.endTime || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="applyPersonName" :label="$t('parkingSpaceApplyManage.applicant')" align="center"
          width="110" />
        <el-table-column prop="applyPersonLink" :label="$t('parkingSpaceApplyManage.phoneNumber')" align="center"
          width="130">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.applyPersonLink || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('parkingSpaceApplyManage.reviewResult')" align="center" width="120">
          <template slot-scope="scope">
            <ds-status-tag size="sm" :type="getStatusTagType(scope.row.state)" :label="_getState(scope.row.state)" />
          </template>
        </el-table-column>
        <el-table-column :label="$t('parkingSpaceApplyManage.operation')" align="center" width="250" fixed="right">
          <template slot-scope="scope">
            <el-button v-if="scope.row.state === '1001'" size="mini" type="primary"
              @click="_openAuditParkingSpaceApplyModal(scope.row)">
              {{ $t('parkingSpaceApplyManage.audit') }}
            </el-button>
            <el-button v-if="scope.row.state === '1001'" size="mini" type="warning"
              @click="_openEditParkingSpaceApplyModel(scope.row)">
              {{ $t('parkingSpaceApplyManage.edit') }}
            </el-button>
            <el-button size="mini" type="danger" @click="_openDeleteParkingSpaceApplyModel(scope.row)">
              {{ $t('parkingSpaceApplyManage.delete') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :page="page.current" :size="page.size" :total="page.total" :page-sizes="[10, 20, 30, 50]"
        @size-change="handleSizeChange" @current-change="handleCurrentChange" />
    </div>

    <edit-parking-space-apply ref="editComponent" @success="handleSuccess" />
    <delete-parking-space-apply ref="deleteComponent" @success="handleSuccess" />
  </div>
</template>

<script>
import { listParkingSpaceApply } from '@/api/car/parkingSpaceApplyManageApi'
import EditParkingSpaceApply from '@/components/car/editParkingSpaceApply'
import DeleteParkingSpaceApply from '@/components/car/deleteParkingSpaceApply'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'

export default {
  name: 'ParkingSpaceApplyManageList',
  components: {
    EditParkingSpaceApply,
    DeleteParkingSpaceApply,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar,
    DsStatusTag
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      page: {
        current: 1,
        size: 10,
        total: 0
      },
      conditions: {
        state: '',
        carNum: '',
        carBrand: '',
        applyPersonName: '',
        applyPersonLink: '',
        communityId: ''
      },
      parkingSpaceApplys: []
    }
  },
  created() {
    this._listParkingSpaceApplys(1, this.page.size)
  },
  methods: {
    async _listParkingSpaceApplys(page, size) {
      this.loading = true
      this.hasError = false
      try {
        const params = {
          ...this.conditions,
          page: page,
          row: size
        }
        const response = await listParkingSpaceApply(params)
        if (response.code === 0) {
          this.parkingSpaceApplys = response.data
          this.page.total = response.total
          this.page.current = page
        } else {
          this.hasError = true
          this.errorMessage = response.msg || this.$t('common.requestFailed')
          this.$message.error(this.errorMessage)
        }
      } catch (error) {
        console.error('Error fetching parking space applications:', error)
        this.hasError = true
        this.errorMessage = this.$t('common.requestError')
        this.$message.error(this.$t('common.requestError'))
      } finally {
        this.loading = false
      }
    },

    _queryParkingSpaceApplyMethod() {
      this._listParkingSpaceApplys(1, this.page.size)
    },

    _resetParkingSpaceApplyMethod() {
      this.conditions = {
        state: '',
        carNum: '',
        carBrand: '',
        applyPersonName: '',
        applyPersonLink: '',
        communityId: ''
      }
      this._listParkingSpaceApplys(1, this.page.size)
    },

    _openAddParkingSpaceApplyModal() {
      // 跳转到申请页面
      this.$router.push('/views/car/addParkingSpaceApply')
    },

    _openAuditParkingSpaceApplyModal(apply) {
      // 跳转到审核页面
      this.$router.push(`/views/car/auditParkingSpaceApply?applyId=${apply.applyId}`)
    },

    _openEditParkingSpaceApplyModel(apply) {
      this.$refs.editComponent.open(apply)
    },

    _openDeleteParkingSpaceApplyModel(apply) {
      this.$refs.deleteComponent.open(apply)
    },

    handleSizeChange(size) {
      this.page.size = size
      this._listParkingSpaceApplys(1, size)
    },

    handleCurrentChange(page) {
      this._listParkingSpaceApplys(page, this.page.size)
    },

    handleSuccess() {
      this._listParkingSpaceApplys(this.page.current, this.page.size)
    },

    getStatusTagType(state) {
      switch (state) {
        case '1001': return 'warning'
        case '2002': return 'primary'
        case '3003': return 'success'
        case '4004': return 'danger'
        default: return 'muted'
      }
    },

    _getState(state) {
      switch (state) {
        case '1001': return this.$t('parkingSpaceApplyManage.pendingReview')
        case '2002': return this.$t('parkingSpaceApplyManage.pendingPayment')
        case '3003': return this.$t('parkingSpaceApplyManage.completed')
        case '4004': return this.$t('parkingSpaceApplyManage.applicationFailed')
        default: return this.$t('parkingSpaceApplyManage.statusAbnormal')
      }
    },

    _getCatType(type) {
      switch (type) {
        case '9901': return this.$t('parkingSpaceApplyManage.homeCar')
        case '9902': return this.$t('parkingSpaceApplyManage.bus')
        case '9903': return this.$t('parkingSpaceApplyManage.truck')
        default: return this.$t('parkingSpaceApplyManage.abnormalVehicle')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.parking-space-apply-manage {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;
  margin-bottom: var(--ds-spacing-lg);

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}
</style>
